import React from 'react';
import { Button, Col, Collapse, Row, Card } from 'antd';
import { useForm, FormProvider, FieldValues } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { CatalogAsyncSelect } from 'components/form/selects/CatalogAsyncSelect';
import { ContentField } from 'components/form/Content/ContentField';
import { FormActions } from 'components/Layout/FormActions/FormActions';
import { InputField } from 'components/form/base/InputField';
import { LangSelectSection } from 'components/form/LangSelectSection/LangSelectSection';
import { MainImage } from 'components/form/MainImage/MainImage';
import { SEO } from 'components/form/SEO/SEO';
import { Select } from 'components/form/base/Select';
import { Status } from 'components/form/Status/Status';
import { TextAreaField } from 'components/form/base/TextAreaField';
import {
  CatalogRegionSelect,
  TRegionType,
} from 'components/form/selects/CatalogRegionSelect';
import {
  ContentTypeEnum,
  TContentBlock,
} from 'components/form/Content/contentTypes';
import { useAppDispatch, useAppSelector } from 'store';
import { savePreviewData } from 'store/slices/preview/actions';
import { sourcePreview } from 'store/slices/preview/interfaces';
import { INewsItem } from 'store/slices/news/interfaces';
import { useDeepEffect } from 'utils/useDeepEffect';
import { isEmpty, isString } from 'utils/helpers';
import { TImage } from 'types/image';
import { TSEO } from 'types/seo';
import { LangEnumKeys } from 'constants/lang';
import { Statuses } from 'constants/status';
import {
  NewsTopicEnum,
  newsTopicNamesHash,
  TNewsTopic,
} from 'constants/newsTopic';

import { mapValues, mapValuesToDraft, validationSchema } from './formUtils';
import { PREVIEW_ASPECT_RATIO } from '../../../constants/image';

interface IFormValues extends TSEO {
  content: TContentBlock[];
  image: TImage;
  intro?: string;
  lang:
    | LangEnumKeys
    | {
        value: LangEnumKeys;
        label: string;
      };
  name: string;
  region: TRegionType;
  shortDescription: string;
  source: string;
  sourceLink: string;
  status: Statuses;
  tags: number[] | { value: number; label: string }[];
  topic: TNewsTopic | { value: TNewsTopic; label: string };
}

type TProps = {
  initialValues: IFormValues;
  onFinish: (values: IFormValues) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSaveDraft?: (values: any) => void;
  cancelButtonHandler: () => void;
  isDraft: boolean;
  isLoading: boolean;
  itemId?: number;
};

const enabledBusinessContentTypes = [
  ContentTypeEnum.image,
  ContentTypeEnum.gallery,
  ContentTypeEnum.video,
  ContentTypeEnum.collage,
];

export const NewsForm: React.FC<TProps> = ({
  initialValues,
  onFinish,
  onSaveDraft,
  cancelButtonHandler,
  isDraft,
  isLoading,
  itemId,
}) => {
  const { b2cUrl, b2bUrl } = useAppSelector(state => state.configSlice);
  const [activeCollpaseTabs, setActiveCollpaseTabs] = React.useState([
    'content',
    'description',
    'seo',
    'status',
  ]);
  const resolver = yupResolver(validationSchema);
  const dispatch = useAppDispatch();
  const methods = useForm({
    defaultValues: initialValues,
    resolver,
  });

  const handleSaveAsDraft = () => {
    const formValues = mapValuesToDraft(methods.getValues());
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSaveDraft(formValues as any);
  };

  const currentLanguage = methods.watch('lang.value');
  const currentTopic = methods.watch('topic.value');

  const handlePreviewClick = async () => {
    const results = await (dispatch(
      savePreviewData({
        source: sourcePreview.news,
        ...(itemId !== undefined && { sourceId: itemId }),
        data: methods.getValues(),
      })
    ) as Promise<{
      payload: { id: number; sysName: string };
    }>);
    const id = results.payload.id;
    const sysName = results.payload.sysName;
    const urlByTopicType = currentTopic === 'business' ? b2bUrl : b2cUrl;
    const linkToPortal =
      currentLanguage === 'en'
        ? `${urlByTopicType}/en/news/preview/${id}/${sysName}`
        : `${urlByTopicType}/news/preview/${id}/${sysName}`;

    window.open(linkToPortal, '_blank');
  };

  const submit = (values: FieldValues) => {
    const preparedValues = mapValues(values) as unknown as INewsItem;

    onFinish(preparedValues);
  };

  useDeepEffect(() => {
    methods.reset(initialValues);
  }, [initialValues]);

  useDeepEffect(() => {
    if (!isEmpty(methods.formState.errors)) {
      setActiveCollpaseTabs([
        ...Array.from(
          new Set([
            ...activeCollpaseTabs,
            ...Object.keys(methods.formState.errors),
          ])
        ),
      ]);
    }
  }, [methods.formState.errors]);

  const lang = methods.watch('lang');
  const topic = methods.watch('topic');
  useDeepEffect(() => {
    if (methods.formState.isDirty) {
      methods.reset({ ...methods.getValues(), tags: [] });
    }
  }, [lang]);

  useDeepEffect(() => {
    if (topic && !isString(topic) && topic.value !== NewsTopicEnum.Tourist) {
      const filteredContent = methods.getValues().content.filter(item => {
        return (
          item.type === ContentTypeEnum.text ||
          enabledBusinessContentTypes.includes(item.type)
        );
      });
      methods.reset({
        ...methods.getValues(),
        content: filteredContent,
      });
    }
  }, [topic]);

  const langValue = isString(lang) ? lang : lang.value;

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(submit)}
        className='ant-form ant-form-vertical indent-top'
      >
        <LangSelectSection />
        <Card title='Общая информация' bordered={false}>
          <Row gutter={[16, 0]}>
            <Col flex={'0 0 100px'}>
              <MainImage
                name='image'
                label='Глав. фото'
                required
                aspectRatio={PREVIEW_ASPECT_RATIO}
              />
            </Col>
            <Col flex={1} style={{ minWidth: 300 }}>
              <InputField
                name='name'
                label='Название публикации'
                required
                placeholder='Введите название'
                showCount
                maxLength={255}
              />
            </Col>
          </Row>
          <Row gutter={[24, 0]}>
            <Col xs={{ span: 24 }} md={{ span: 14 }}>
              <Select
                name='topic'
                label='Тема/рубрика'
                required
                placeholder='Выберите тему'
                options={[
                  ...Object.keys(newsTopicNamesHash).map(item => ({
                    value: item,
                    label: newsTopicNamesHash[item],
                  })),
                ]}
              />
            </Col>
            <Col xs={{ span: 24 }} md={{ span: 10 }}>
              <CatalogRegionSelect
                name='region'
                label='Регион'
                placeholder='Выберите регион'
                isNewsPage
                // required
              />
            </Col>
          </Row>
          <Row gutter={[24, 0]}>
            <Col span={24}>
              <CatalogAsyncSelect
                name='tags'
                label='Теги'
                placeholder='Выберите теги'
                requestParams={{
                  entityType: 'news',
                  fieldType: 'tags',
                  lang: langValue,
                }}
                mode='multiple'
              />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <InputField
                name='shortDescription'
                label='Краткое описание'
                required
                placeholder='Введите описание'
                showCount
                maxLength={300}
              />
            </Col>
          </Row>
        </Card>
        <Collapse
          bordered={false}
          expandIconPosition='end'
          onChange={value => setActiveCollpaseTabs(value as string[])}
          activeKey={activeCollpaseTabs}
        >
          <Collapse.Panel header='Описание публикации' key='content'>
            <Row>
              <Col span={24}>
                <TextAreaField
                  label='Введение'
                  name='intro'
                  placeholder='Текст'
                  showCount
                  maxLength={700}
                  rows={10}
                />
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <ContentField
                  name='content'
                  label='Описание'
                  placeholder='Начните печатать текст'
                  withWidgetsDescription
                  withPreparingForModeration
                  required
                  {...(!isString(topic) &&
                    topic?.value !== NewsTopicEnum.Tourist && {
                      enabledTypes: enabledBusinessContentTypes,
                    })}
                />
              </Col>
            </Row>
          </Collapse.Panel>
          <Collapse.Panel header='SEO' key='seo'>
            <SEO />
          </Collapse.Panel>
          <Collapse.Panel header='Публикация' key='status'>
            <Status
              status={initialValues.status}
              withSourceInput
              isStatusesLimited
            />
          </Collapse.Panel>
        </Collapse>
        <FormActions>
          <Button onClick={cancelButtonHandler} disabled={isLoading}>
            Отмена
          </Button>
          {isDraft ? (
            <Button onClick={handleSaveAsDraft} loading={isLoading}>
              Сохранить как черновик
            </Button>
          ) : null}

          <Button onClick={handlePreviewClick} disabled={isLoading}>
            Предпросмотр
          </Button>
          <Button type='primary' htmlType='submit' loading={isLoading}>
            Сохранить
          </Button>
        </FormActions>
      </form>
    </FormProvider>
  );
};
